import { useState, useEffect } from "react";
import axios from "axios";
import { FaDollarSign, FaSpinner, FaBoxes, FaStore, FaMapMarkerAlt } from "react-icons/fa";
import { UploadButton } from "./supply-chain/SupplyChainAnalytics.jsx";

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

/**
 * Standalone Part Costs page (Veeco item 10) — NO play gating.
 * Reuses the existing supplier-bom upload pipeline (upload + clarification
 * round-trip via the shared UploadButton) and shows a summary card.
 */
export default function PartCosts() {
  const token = sessionStorage.getItem("userToken");

  const [spaces, setSpaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState("");
  const [selectedProject, setSelectedProject] = useState("");

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  // Load scope options (spaces + projects) for the upload context selectors
  useEffect(() => {
    const loadScope = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/ocr/reports/scope-options/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSpaces(res.data.spaces || []);
        setProjects(res.data.projects || []);
      } catch {
        /* selectors just stay empty */
      }
    };
    loadScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${BASE_URL}/ocr/part-costs/summary/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data);
    } catch {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projectsInSpace = projects.filter(
    (p) => !selectedSpace || p.space_path === selectedSpace
  );
  const canUpload = Boolean(selectedSpace && selectedProject);

  const Stat = ({ icon, label, value }) => (
    <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3">
      <div className="p-2 bg-green-100 rounded-lg text-green-700">{icon}</div>
      <div>
        <div className="text-xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <FaDollarSign className="w-6 h-6 text-green-700" />
        <h1 className="text-2xl font-semibold text-gray-900">Part Costs</h1>
      </div>

      {/* Upload card */}
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <p className="text-sm text-gray-600">
          Upload purchase history or supplier quotes. Costs auto-match to drawings by part
          number and appear in the tariff calculator and report builder.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Space</label>
            <select
              value={selectedSpace}
              onChange={(e) => {
                setSelectedSpace(e.target.value);
                setSelectedProject("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select a space…</option>
              {spaces.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              disabled={!selectedSpace}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            >
              <option value="">Select a project…</option>
              {projectsInSpace.map((p) => (
                <option key={p.id} value={p.folder_name}>
                  {p.folder_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          {canUpload ? (
            <UploadButton
              spacePath={selectedSpace}
              folderName={selectedProject}
              onSuccess={fetchSummary}
              label="Upload Part Costs"
            />
          ) : (
            <p className="text-xs text-gray-400">
              Select a space and project to enable upload.
            </p>
          )}
        </div>
      </div>

      {/* Summary card */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Current part-cost data
        </h2>
        {loadingSummary ? (
          <div className="flex items-center gap-2 text-gray-400 py-6">
            <FaSpinner className="animate-spin" /> Loading…
          </div>
        ) : !summary || summary.total_rows === 0 ? (
          <p className="text-sm text-gray-400">
            No part-cost data yet. Upload a spreadsheet above to get started.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={<FaBoxes />} label="Rows" value={summary.total_rows} />
              <Stat icon={<FaBoxes />} label="Distinct parts" value={summary.distinct_parts} />
              <Stat icon={<FaStore />} label="Suppliers" value={summary.distinct_suppliers} />
              <Stat
                icon={<FaMapMarkerAlt />}
                label="Sites"
                value={summary.sites?.length || 0}
              />
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-sm text-gray-600 space-y-1">
              <div>
                <span className="font-medium text-gray-800">Actual spend:</span>{" "}
                {summary.by_record_type?.actual_spend ?? 0} ·{" "}
                <span className="font-medium text-gray-800">Info records:</span>{" "}
                {summary.by_record_type?.inforecord ?? 0}
              </div>
              {summary.sites?.length > 0 && (
                <div>
                  <span className="font-medium text-gray-800">Sites:</span>{" "}
                  {summary.sites.join(", ")}
                </div>
              )}
              {summary.last_upload?.uploaded_by && (
                <div className="text-xs text-gray-400">
                  Last upload by {summary.last_upload.uploaded_by}
                  {summary.last_upload.source_file
                    ? ` · ${summary.last_upload.source_file}`
                    : ""}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
